import { Controller } from '@hotwired/stimulus';
import type { Application } from '@hotwired/stimulus';

interface TooltipPasswordStrengthOptions {
    strength: string[];
    validationRegex: string;
}

type ValuesItem<D, T = String | Object | Boolean> = {
    type: T;
    default: D;
};

type Values = {
    options: ValuesItem<TooltipPasswordStrengthOptions, Object>;
};

/**
 * Add password strength hints to a field. **Requires Dropbox's zxcvbn package** to be installed via npm.
 * @example
 * ```html
 * <div data-controller="password-strength" data-password-strength-options="{}">
 *     <input type="password" data-action="password-field#checkInputValue" />
 *     <div data-password-strength-target="tooltip" role="tooltip">
 *         <p data-password-strength-target="title">Passwort Strength</p>
 *         <button type="button" data-action="password-strength#toggleHints">Toggle hints</button>
 *         <ul data-password-strength-target="hints">
 *            Any form of hints
 *         </ul>
 *         <meter data-password-strength-target="meter"></meter>
 *     </div>
 * </div>
 * ```
 */
export class PasswordStrengthFormFieldController extends Controller<HTMLElement> {
    declare optionsValue: TooltipPasswordStrengthOptions;

    declare tooltipTarget: HTMLElement;
    declare readonly hasTooltipTarget: boolean;

    declare hintsTarget: HTMLElement;
    declare readonly hasHintsTarget: boolean;

    declare titleTarget: HTMLElement;
    declare readonly hasTitleTarget: boolean;

    declare inputTarget: HTMLInputElement;
    declare readonly hasInputTarget: boolean;

    declare meterTarget: HTMLMeterElement;
    declare readonly hasMeterTarget: boolean;

    declare initalizedClasses: string[];
    declare readonly hasInitalizedClass: boolean;

    static readonly componentName = 'password-strength';

    static values: Values = {
        options: {
            type: Object,
            default: {
                strength: ['Worst', 'Bad', 'Weak', 'Good', 'Strong'],
                validationRegex: '^.{8,64}$',
            },
        },
    };

    static targets = ['tooltip', 'title', 'hints', 'meter'];

    private zxcvbnInstance: (password: string, user_inputs?: []) => { score: number };
    private validationRegex: RegExp;

    /**
     * Register the stimulus controller on the application
     */
    static register(application: Application) {
        application.register(PasswordStrengthFormFieldController.componentName, this);
    }

    async connect(): Promise<void> {
        if (this.hasHintsTarget) {
            this.toggleHints(false);
        }

        if (this.hasTooltipTarget) {
            this.tooltipTarget.classList.add('klt-c-form-field-tooltip--js');
        }

        const zxcvbnModule = await import('zxcvbn');
        this.zxcvbnInstance = zxcvbnModule.default;
        this.validationRegex = RegExp(this.optionsValue.validationRegex);

        console.log(this.zxcvbnInstance);
    }

    checkInputValue(e: InputEvent) {
        const inputElement = e.target as HTMLInputElement;
        const value = inputElement.value;

        let score = this.getPasswordStregthScore(value);

        if (this.validationRegex) {
            if (!this.validationRegex.test(value)) {
                score = 0;
            }
        }

        this.meterTarget.value = score;

        if (this.hasTitleTarget && this.optionsValue?.strength) {
            this.titleTarget.innerHTML = this.meterTarget.value !== undefined ? this.optionsValue.strength[score] : '';
        }
    }

    getPasswordStregthScore(value: string): number {
        const result = this.zxcvbnInstance(value);
        return result.score;
    }

    toggleHints(state?: boolean) {
        // Toggle visibility of hints on trigger click
        const prevValue = this.hintsTarget.getAttribute('aria-hidden') === 'true';
        const newValue = state !== undefined ? state : !prevValue;

        this.hintsTarget.setAttribute('aria-hidden', newValue.toString());
    }
}
